import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2 } from "lucide-react";
import { formatApiError } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { name: "", client_code: "", gstin: "", pan: "" };

/**
 * Lightweight "Add Client" dialog used from the sidebar.
 * Captures the essentials only — for advanced fields (contact, branches,
 * FY start), open the full Clients page from the Admin sidebar section.
 */
export default function QuickAddClientDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const reset = () => setForm(EMPTY);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        client_code: form.client_code.trim() || null,
        gstin: form.gstin.trim().toUpperCase() || null,
        pan: form.pan.trim().toUpperCase() || null,
        fy_start_month: 4,
        has_branches: false,
        branches: [],
      };
      const res = await axios.post(`${API}/companies`, payload);
      toast.success(`Client "${payload.name}" created`);
      reset();
      onOpenChange(false);
      onCreated && onCreated(res.data?.company_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!saving) { onOpenChange(v); if (!v) reset(); } }}
    >
      <DialogContent className="sm:max-w-[460px]" data-testid="quick-add-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Add new client
          </DialogTitle>
          <DialogDescription className="text-xs">
            Each client has isolated COA, TB, vouchers and MIS. For contacts,
            branches, or FY start month, use the full <strong>Clients</strong>{" "}
            page under Admin.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div>
            <Label htmlFor="qac-name" className="text-xs">Client name *</Label>
            <Input
              id="qac-name"
              data-testid="quick-add-client-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. ACME Industries Pvt Ltd"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="qac-code" className="text-xs">Client code</Label>
              <Input
                id="qac-code"
                data-testid="quick-add-client-code"
                value={form.client_code}
                onChange={(e) => setForm({ ...form, client_code: e.target.value })}
                placeholder="ACME-01"
              />
            </div>
            <div>
              <Label htmlFor="qac-gstin" className="text-xs">GSTIN</Label>
              <Input
                id="qac-gstin"
                data-testid="quick-add-client-gstin"
                value={form.gstin}
                onChange={(e) => setForm({ ...form, gstin: e.target.value })}
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="qac-pan" className="text-xs">PAN</Label>
            <Input
              id="qac-pan"
              data-testid="quick-add-client-pan"
              value={form.pan}
              onChange={(e) => setForm({ ...form, pan: e.target.value })}
              placeholder="AAAAA0000A"
              maxLength={10}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); reset(); }}
            disabled={saving}
            data-testid="quick-add-client-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !form.name.trim()}
            data-testid="quick-add-client-submit"
            className="bg-zinc-950 text-white hover:bg-zinc-800"
          >
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Saving…</> : "Create client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
